import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { useSeoHead } from "@/lib/head";
import { useSite } from "@/context/SiteContext";
import { Breadcrumbs } from "@/components/public/PageParts";
import Reveal from "@/components/public/Reveal";
import PreviewBanner from "@/components/public/PreviewBanner";
import NotFound from "./NotFound";

function fmtDate(d, lang) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(lang === "tr" ? "tr-TR" : lang, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return d; }
}

export default function BlogIndex() {
  const { lang } = useParams();
  const { setSwitcher } = useSite();
  const [sp, setSp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const activeCat = sp.get("cat") || "all";
  const withPrev = (p) => (preview ? `${p.replace(/\/$/, "")}?preview=1` : p.replace(/\/$/, ""));

  const { data: page, isError, isLoading } = useQuery({
    queryKey: ["blog-index", lang, preview ? "prev" : "pub"],
    queryFn: async () => preview
      ? (await api.get(`/preview/blog/${lang}`)).data
      : (await api.get(`/public/blog/${lang}`)).data,
    retry: false,
  });

  useSeoHead(page);
  useEffect(() => { if (page?.switcher) setSwitcher(page.switcher); return () => setSwitcher(null); }, [page, setSwitcher]);

  const categories = page?.categories || [];
  const posts = useMemo(() => {
    const all = page?.posts || [];
    return activeCat === "all" ? all : all.filter((p) => p.category === activeCat);
  }, [page, activeCat]);

  const setCat = (key) => {
    const next = new URLSearchParams(sp);
    if (key === "all") next.delete("cat"); else next.set("cat", key);
    setSp(next, { replace: true });
  };

  if (isLoading) return <div className="container-x py-32 text-subtle">…</div>;
  if (isError || !page) return <NotFound />;

  const c = page.content;
  const chip = (act) => `text-xs px-4 py-2 rounded-full border transition-colors ${act ? "border-copper text-copper bg-copper/10" : "border-white/20 text-subtle hover:text-ink"}`;

  return (
    <div className="overflow-x-hidden">
      {page.preview && <PreviewBanner lang={lang} status={page.preview_meta?.status} updatedAt={page.preview_meta?.updated_at} />}
      <header className="relative pt-28 pb-12 border-b border-white/10 bg-graphite/40">
        <div className="container-x">
          <Breadcrumbs crumbs={c.breadcrumbs} dir={page.dir} />
          <h1 data-testid="blog-index-h1" className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink mt-5">{c.h1}</h1>
          <p className="text-subtle text-lg mt-5 max-w-2xl leading-relaxed">{c.intro}</p>
          <div className="w-16 h-1 bg-copper mt-6 rounded-full" />
        </div>
      </header>

      <section className="container-x py-12 md:py-16">
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2.5 mb-10" data-testid="blog-category-filter">
            <button data-testid="blog-cat-all" onClick={() => setCat("all")} className={chip(activeCat === "all")}>{t(lang, "allCategories")}</button>
            {categories.map((cat) => (
              <button key={cat.key} data-testid={`blog-cat-${cat.key}`} onClick={() => setCat(cat.key)} className={chip(activeCat === cat.key)}>{cat.label}</button>
            ))}
          </div>
        )}

        {posts.length === 0 ? (
          <p className="text-subtle" data-testid="blog-empty">{t(lang, "blogEmpty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
            {posts.map((p, i) => (
              <Reveal key={p.entity_id} dir={i % 3 === 0 ? "left" : i % 3 === 2 ? "right" : "up"} delay={(i % 3) * 80}>
                <Link
                  to={withPrev(p.path)}
                  data-testid={`blog-card-${p.entity_id}`}
                  className="group flex flex-col h-full rounded-2xl overflow-hidden border border-white/10 hover:border-copper/60 bg-graphite/40 transition-colors duration-300"
                >
                  <div className="relative h-52 overflow-hidden">
                    {p.image && <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent" />
                    {p.category_label && <span className="absolute top-3 left-3 rtl:left-auto rtl:right-3 text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-obsidian/70 backdrop-blur text-copper">{p.category_label}</span>}
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    {p.date && <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-faint mb-3"><CalendarDays size={13} className="text-copper" /> {fmtDate(p.date, lang)}</p>}
                    <h2 className="text-lg font-bold text-ink leading-snug group-hover:text-copper transition-colors">{p.title}</h2>
                    <p className="text-subtle text-sm mt-3 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-copper text-sm font-semibold mt-4">{t(lang, "readMore")} <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
